import React, { FC, memo, useMemo } from "react";
// components
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import ContentItem from "./ContentItem";
// styles
import {
    AnimatedText,
    ContentListContainer,
    DefaultHeaderContainer,
    HalfCardContainer,
    HalfCardDetails,
    LottiContainer,
    SwiggyLogo,
    TitleText,
} from "../styles";
// types
import { BottomSheetData } from "@super-ui/components/m2c-swiggy-black-halfcard/types";
import { Content, M2cTypes, AssetCtx } from "@swiggy-private/cart-types";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { getCloudinaryUrl, LottieAnimation } from "@swiggy-private/core-ui";
// constants
import {
    M2C_HALFCARD_TEST_IDS,
    SWIGGY_LOGO_PROPS,
    CLUSTER_STARS,
    M2C_BLACK_TITLE_TEXT_COLOR,
} from "@super-ui/components/m2c-swiggy-black-halfcard/constants";
import { PLACEHOLDERS } from "@super-ui/constants";

const VariantV1: FC<BottomSheetData> = ({ title, content, logoCtx, identifiers, assetCtx }) => {
    const isBlackRenewalType = useMemo(() => identifiers?.m2c_type?.toUpperCase() === M2cTypes.M2C_BLACK_RENEWAL, [
        identifiers?.m2c_type,
    ]);

    const contentContainer = useMemo(
        () =>
            !isEmpty(content) && typeof content !== "string" ? (
                <ContentListContainer
                    isBlackRenewalType={isBlackRenewalType}
                    data-testid={M2C_HALFCARD_TEST_IDS.CONTENT}
                >
                    {content?.map((content: Content, index) => (
                        <ContentItem key={index} {...content} isBlackRenewalType={isBlackRenewalType} />
                    ))}
                </ContentListContainer>
            ) : null,
        [content, isBlackRenewalType],
    );

    return (
        <>
            <HalfCardContainer>
                {!isEmpty(logoCtx?.logo) && (
                    <SwiggyLogo data-testid={M2C_HALFCARD_TEST_IDS.LOGO} isBlackRenewalType={isBlackRenewalType}>
                        <Image cloudinaryId={logoCtx?.logo} {...SWIGGY_LOGO_PROPS} />
                    </SwiggyLogo>
                )}

                {isBlackRenewalType && (
                    <>
                        <TitleText
                            data-testid={M2C_HALFCARD_TEST_IDS.TITLE}
                            variant="V2.overline.neutral.bold_spaced"
                            customColor={{
                                light: M2C_BLACK_TITLE_TEXT_COLOR,
                                dark: M2C_BLACK_TITLE_TEXT_COLOR,
                            }}
                            dangerouslySetInnerHTML={{ __html: title?.toLocaleUpperCase() }}
                        />

                        {!isEmpty(assetCtx) &&
                            assetCtx.map((asset: AssetCtx) => (
                                <LottiContainer key={asset?.link}>
                                    <LottieAnimation
                                        fileName={getCloudinaryUrl(asset?.link)}
                                        visible={true}
                                        backwardSpeed={1}
                                        forwardSpeed={1}
                                        playLottie={true}
                                        playIfVisible={true}
                                        loop={false}
                                    />
                                </LottiContainer>
                            ))}

                        {contentContainer}
                    </>
                )}

                {!isBlackRenewalType && (
                    <HalfCardDetails>
                        <DefaultHeaderContainer>
                            {title?.split(PLACEHOLDERS.ANIMATE_IDENTIFIER).map((text, index) => (
                                <AnimatedText
                                    key={index}
                                    index={index}
                                    variant="V2.overline.neutral.bold_spaced"
                                    color="primary"
                                    data-testid={M2C_HALFCARD_TEST_IDS.TITLE}
                                >
                                    {text}
                                    <Image cloudinaryId={CLUSTER_STARS} width="14px" height="14px" />
                                </AnimatedText>
                            ))}
                        </DefaultHeaderContainer>
                        {contentContainer}
                    </HalfCardDetails>
                )}
            </HalfCardContainer>
        </>
    );
};

export default memo(VariantV1);
